import * as R from 'ramda';

import { ErplyAttributes, rowAttributeRegex } from 'utils/ErplyAttributes';
import { SaleDocumentResponse, SaleDocumentRow } from 'types/SalesDocument';
import { Order } from 'types/ShoppingCart';
import { InvoiceRowRecord as JdocType } from 'services/JsonAPI';

const escapeRegex = /[-/\\^$*+?.()|[\]{}]/g;
const leadingSpaceRegex = /^\s/;

type CustomPromptDetails = { [key: string]: string };
export type CustomPromptAndSeparatorResponse =
  | undefined
  | {
      customPromptDetails: CustomPromptDetails;
      separator: string;
    };
type CartItem = Partial<SaleDocumentRow> & {
  name: string;
  attributes?: {
    customPromptDetails?: string | CustomPromptDetails;
    serialNumberSeparator?: string;
    [otherAttr: string]: any;
  };
} & Partial<JdocType>;

/**
 * This function uses the SERIAL NUMBER SEPARATOR despite being custom prompts specific - this is due to the fact that existing functionality already uses that, so if it is updated, make sure this function is also updated
 */
export function getCustomPromptValuesAndSeparatorUsedFromItem({
  item,
  separator,
  customPrompts,
}: {
  item: CartItem;
  separator: string;
  customPrompts?: null | string[];
}): CustomPromptAndSeparatorResponse {
  if (!customPrompts) {
    return undefined;
  }
  // First attempt to read new attributes
  const { attributes } = item;
  const {
    customPromptDetails: promptDataOnSave,
    serialNumberSeparator: snSeparatorOnSave,
  } = attributes ?? {};
  // Custom prompts were filled out on the new version, which is saved as attribute - no need to parse prompt values from name
  // Check for string specifically since separator may be ''
  if (promptDataOnSave && typeof snSeparatorOnSave === 'string') {
    const parsePromptData = () => {
      if (typeof promptDataOnSave === 'string') {
        try {
          return JSON.parse(promptDataOnSave);
        } catch {
          return {};
        }
      }
      return promptDataOnSave;
    };
    return {
      customPromptDetails: parsePromptData(),
      separator: snSeparatorOnSave,
    };
  }
  const itemHasSerialNumber = !!item.serialNumber;
  const [openSymbol = '', closeSymbol = ''] = separator.split(',');
  const escapedOpenSymbol = openSymbol.replace(escapeRegex, '\\$&');
  const escapedCloseSymbol = closeSymbol.replace(escapeRegex, '\\$&');

  const excludingCloseSymbol = `[^${escapedCloseSymbol}]*`;
  const pattern = new RegExp(
    `${escapedOpenSymbol}(.*?)${escapedCloseSymbol}${excludingCloseSymbol}$`,
  );

  const match = item.name.match(pattern);
  if (match) {
    const result: CustomPromptDetails = {};
    match[1]
      .trim()
      .split(',')
      .forEach((keyval: string, index) => {
        const [key, val] = keyval.trim().split(':');
        if (!key) {
          // do nothing
        } else if (customPrompts.includes(key)) {
          // After splitting, value would have a leading space (' value') - since we seemingly allow empty values for custom prompts, this is needed to remove ONLY the leading space
          result[key] = (val ?? '').replace(leadingSpaceRegex, '');
        } else if (
          // either not first key
          index > 0 ||
          // if item had serial number assigned, it goes first in the name - thus if item had none, then it is safe to assume that this is part of custom prompt
          !itemHasSerialNumber
        ) {
          // After splitting, value would have a leading space (' value') - since we seemingly allow empty values for custom prompts, this is needed to remove ONLY the leading space
          result[key] = (val ?? '').replace(leadingSpaceRegex, '');
        }
      });
    return { customPromptDetails: result, separator };
  }
  return undefined;
}

type Props = {
  product: Order;
  separator: string;
  newValues: { [key: string]: string | number };
  /** Provided only on new product addition (addProduct) and when editing the SN */
  serialNumber?: string;
  existingSerialNumber?: string;
};
export function getEmbeddedProductWithCustomPromptInformation({
  product,
  separator,
  newValues,
  serialNumber = '',
  existingSerialNumber = '',
}: Props): Order {
  const [openSymbol = '', closeSymbol = ''] = separator.split(',');

  const escapedOpenSymbol = openSymbol.replace(escapeRegex, '\\$&');
  const escapedCloseSymbol = closeSymbol.replace(escapeRegex, '\\$&');

  let newName = product.name;
  const existingKeys: string[] = [];

  if (serialNumber.length) {
    // If updating serial number - literally replace the previous serial number withing the name
    if (existingSerialNumber.length) {
      const serialNumberPattern = new RegExp(
        `${escapedOpenSymbol}${existingSerialNumber.replace(
          escapeRegex,
          '\\$&',
        )}`,
        'g',
      );
      // Only update if new one doesn't match the old one
      if (serialNumber !== existingSerialNumber) {
        const newSN = `${serialNumber}`;
        newName = newName.replace(serialNumberPattern, `${openSymbol}${newSN}`);
      }
    }
    // No existing serial number - product is being added
    else {
      newName = `${newName} ${openSymbol}${serialNumber}${closeSymbol}`;
    }
  }

  const newKeys = Object.keys(newValues);
  newKeys.forEach((key, index) => {
    const isLast = index === newKeys.length - 1;
    const escapedKey = key.replace(escapeRegex, '\\$&');
    // Capture the key/value pair via regex for the custom prompt key (capture up to the next comma)
    const keyPattern = new RegExp(
      `(${escapedKey}:\\s*)([^,]*)(${isLast ? escapedCloseSymbol : ''})`,
    );
    if (keyPattern.test(newName)) {
      newName = newName.replace(keyPattern, `$1${newValues[key]}$3`);
      existingKeys.push(key);
    }
  });

  const newPrompts = newKeys
    .filter(key => !existingKeys.includes(key))
    .map(key => `${key}: ${newValues[key]}`)
    .join(', ');

  if (newPrompts.length > 0) {
    if (escapedOpenSymbol && new RegExp(`${escapedOpenSymbol}`).test(newName)) {
      const regexToGetAllMatches = new RegExp(
        `(${escapedOpenSymbol}[^${escapedCloseSymbol}]*)(${escapedCloseSymbol})`,
        'g',
      );
      // Check if Product also contains some pre-existing note, which uses the same open/close symbols if so- replace only latter part
      const match = newName.match(regexToGetAllMatches);
      if (match && match.length > 1) {
        newName = newName.replace(match[1], `${match[1]}, ${newPrompts}`);
      } else {
        const replacementRegex = new RegExp(
          `(${escapedOpenSymbol}[^${escapedCloseSymbol}]*)(${escapedCloseSymbol})`,
        );
        newName = newName.replace(replacementRegex, `$1, ${newPrompts}$2`);
      }
    } else {
      newName = `${newName.trim()} ${openSymbol}${newPrompts}${closeSymbol}`;
    }
  }
  // Since the custom prompts are captured up to the next comma (if any, there'll be no close symbol so add it)
  if (!new RegExp(`${escapedCloseSymbol}$`).test(newName)) {
    // Ensure that the closing symbol is correctly appended
    newName = `${newName.trim()}${closeSymbol}`;
  }

  const attributes = new ErplyAttributes(product.attributes);
  attributes.set('customPromptDetails', JSON.stringify(newValues));
  attributes.set('serialNumberSeparator', separator);

  // Need to remove the title part and save the actual value to the product's `serialNumber`
  const snToReturn =
    (serialNumber.length ? serialNumber : existingSerialNumber)?.split(
      ': ',
    )?.[1] ?? existingSerialNumber;

  return R.pipe(
    R.assoc('attributes', attributes.asDict),
    R.assoc('name', newName),
    R.assoc('serialNumber', snToReturn),
  )(product) as Order;
}

function parseJsonIfPossible(value: string) {
  try {
    return JSON.parse(value);
  } catch (e) {
    // value is not JSON, return as is
    return value;
  }
}

export function getRowAttributesPerRowIdFromSale(
  salesDocument: SaleDocumentResponse,
): {
  [rowId: string]: { [attrName: string]: string | { [attrName: string]: any } };
} {
  const attributesPerRowId = {};
  const { attributes: saleAttributes = {} } = salesDocument;
  const erplyfiedAttributes = new ErplyAttributes(saleAttributes);
  erplyfiedAttributes.asArray.forEach(({ attributeName, attributeValue }) => {
    const match = attributeName.match(rowAttributeRegex);
    if (match) {
      const [, rowID, attrName] = match;
      attributesPerRowId[rowID] = {
        ...attributesPerRowId[rowID],
        [attrName]: parseJsonIfPossible(attributeValue),
      };
    }
  });

  return attributesPerRowId;
}
